<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import AgenteIaAPI from 'dashboard/api/agenteIa';
import Button from 'dashboard/components-next/button/Button.vue';
import AgenteIaLayout from '../components/AgenteIaLayout.vue';
import Avisos from '../components/Avisos.vue';

const { t } = useI18n();
const route = useRoute();
const carregando = ref(true);
const erro = ref('');
const bases = ref([]);
const limites = ref({});
const baseAtual = ref(null);
const documentos = ref([]);
const bloqueio = ref(null);
const aprovacoes = ref([]);
const novaBase = ref('');
const novoDocumento = ref({ titulo: '', texto: '' });
const enviando = ref(false);
const arquivoInput = ref(null);
const aba = ref('agente');
const editorAberto = ref(false);
const editando = ref(null);
const excluindo = ref(null);
const basesDaAba = computed(() =>
  bases.value.filter(base => base.equipe === (aba.value === 'equipe'))
);

const falhou = e =>
  useAlert(e?.response?.data?.error || t('AGENTE_IA.BASE.ERRO'));

const abrirBase = async base => {
  editorAberto.value = false;
  editando.value = null;
  excluindo.value = null;
  novoDocumento.value = { titulo: '', texto: '' };
  baseAtual.value = base;
  const { data } = await AgenteIaAPI.documentos(base.id);
  documentos.value = data.payload;
  bloqueio.value = data.bloqueio;
};

const carregar = async () => {
  try {
    const [{ data }, { data: fila }] = await Promise.all([
      AgenteIaAPI.bases(),
      AgenteIaAPI.aprovacoes(),
    ]);
    bases.value = data.payload;
    limites.value = data.limites;
    aprovacoes.value = fila.payload;
    if (basesDaAba.value.length) await abrirBase(basesDaAba.value[0]);
  } catch (e) {
    erro.value = e?.response?.data?.error || t('AGENTE_IA.BASE.ERRO');
  } finally {
    carregando.value = false;
  }
};

onMounted(carregar);

const podeCriarBase = computed(
  () =>
    bases.value.filter(base => !base.equipe).length < (limites.value.bases || 0)
);

const trocarAba = async nova => {
  editorAberto.value = false;
  excluindo.value = null;
  aba.value = nova;
  baseAtual.value = null;
  documentos.value = [];
  bloqueio.value = null;
  if (nova !== 'avisos' && basesDaAba.value.length) {
    await abrirBase(basesDaAba.value[0]);
  }
};

const criarBase = async () => {
  if (!novaBase.value.trim()) return;
  try {
    const { data } = await AgenteIaAPI.criarBase({
      name: novaBase.value.trim(),
    });
    bases.value = data.payload;
    novaBase.value = '';
    await abrirBase(bases.value[bases.value.length - 1]);
  } catch (e) {
    falhou(e);
  }
};

const criarDocumento = async () => {
  enviando.value = true;
  try {
    if (!baseAtual.value && aba.value === 'equipe') {
      const { data } = await AgenteIaAPI.criarBase({ equipe: true });
      bases.value = data.payload;
      await abrirBase(basesDaAba.value[0]);
    }
    editando.value = null;
    novoDocumento.value = { titulo: '', texto: '' };
    editorAberto.value = true;
  } catch (e) {
    falhou(e);
  } finally {
    enviando.value = false;
  }
};

const editarDocumento = async documento => {
  enviando.value = true;
  try {
    const { data } = await AgenteIaAPI.documento(documento.id);
    novoDocumento.value = { titulo: data.title, texto: data.text };
    editando.value = documento.id;
    editorAberto.value = true;
  } catch (e) {
    falhou(e);
  } finally {
    enviando.value = false;
  }
};

const excluirDocumento = async documento => {
  enviando.value = true;
  try {
    await AgenteIaAPI.apagarDocumento(documento.id);
    documentos.value = documentos.value.filter(
      item => item.id !== documento.id
    );
    excluindo.value = null;
    if (editando.value === documento.id) editorAberto.value = false;
  } catch (e) {
    falhou(e);
  } finally {
    enviando.value = false;
  }
};

const enviar = async arquivo => {
  enviando.value = true;
  try {
    if (editando.value && !arquivo) {
      await AgenteIaAPI.atualizarDocumento(editando.value, novoDocumento.value);
      const { data } = await AgenteIaAPI.documentos(baseAtual.value.id);
      documentos.value = data.payload;
    } else {
      const { data } = await AgenteIaAPI.enviarDocumento(
        baseAtual.value.id,
        arquivo ? { arquivo } : novoDocumento.value
      );
      documentos.value = data.payload;
    }
    editorAberto.value = false;
    editando.value = null;
    novoDocumento.value = { titulo: '', texto: '' };
    useAlert(t('AGENTE_IA.BASE.ENVIADO'));
  } catch (e) {
    falhou(e);
  } finally {
    enviando.value = false;
  }
};

const escolherArquivo = evento => {
  const [arquivo] = evento.target.files || [];
  if (arquivo) enviar(arquivo);
  evento.target.value = '';
};

// O motivo da sugestao cita a conversa-fonte ("Conversa #12"): o numero e o display_id.
const conversaDoMotivo = motivo => motivo?.match(/Conversa #(\d+)/)?.[1];

const decidir = async (aprovacao, aprovar) => {
  try {
    const { data } = await AgenteIaAPI.decidirAprovacao(aprovacao.id, aprovar);
    aprovacoes.value = data.payload;
  } catch (e) {
    falhou(e);
  }
};

const classeCampo =
  'w-full px-3 py-2 text-sm border rounded-lg bg-n-alpha-black2 border-n-weak text-n-slate-12';
</script>

<template>
  <AgenteIaLayout
    :title="t('AGENTE_IA.BASE.TITULO')"
    :description="t('AGENTE_IA.BASE.DESCRICAO')"
  >
    <p v-if="carregando" class="text-sm text-n-slate-11">
      {{ t('AGENTE_IA.CARREGANDO') }}
    </p>
    <p v-else-if="erro" class="text-sm text-n-ruby-11">{{ erro }}</p>

    <template v-else>
      <section class="flex flex-col gap-2">
        <div class="flex gap-2">
          <Button
            size="sm"
            :variant="aba === 'agente' ? 'solid' : 'outline'"
            :label="t('AGENTE_IA.BASE.ABAS.AGENTE')"
            :disabled="enviando"
            @click="trocarAba('agente')"
          />
          <Button
            size="sm"
            :variant="aba === 'equipe' ? 'solid' : 'outline'"
            :label="t('AGENTE_IA.BASE.ABAS.EQUIPE')"
            :disabled="enviando"
            @click="trocarAba('equipe')"
          />
          <Button
            size="sm"
            :variant="aba === 'avisos' ? 'solid' : 'outline'"
            :label="t('AGENTE_IA.BASE.ABAS.AVISOS')"
            :disabled="enviando"
            @click="trocarAba('avisos')"
          />
        </div>
      </section>

      <Avisos v-if="aba === 'avisos'" />

      <section v-if="aba !== 'avisos'" class="flex flex-col gap-2">
        <div class="flex flex-wrap gap-2">
          <Button
            v-for="base in basesDaAba"
            :key="base.id"
            size="sm"
            :variant="baseAtual?.id === base.id ? 'solid' : 'outline'"
            :label="`${base.name} (${base.documentos})`"
            :disabled="enviando"
            @click="abrirBase(base)"
          />
        </div>
        <form
          v-if="aba === 'agente' && podeCriarBase"
          class="flex gap-2"
          @submit.prevent="criarBase"
        >
          <input
            v-model="novaBase"
            :class="classeCampo"
            class="sm:w-80"
            :placeholder="t('AGENTE_IA.BASE.NOVA_BASE')"
          />
          <Button
            type="submit"
            size="sm"
            :label="t('AGENTE_IA.BASE.CRIAR')"
            :disabled="enviando"
          />
        </form>
        <p v-if="aba === 'agente'" class="text-xs text-n-slate-11">
          {{
            t('AGENTE_IA.BASE.LIMITES', {
              bases: limites.bases,
              documentos: limites.documentos,
            })
          }}
        </p>
        <template v-else>
          <p class="text-xs text-n-slate-11">
            {{ t('AGENTE_IA.BASE.EQUIPE_AJUDA') }}
          </p>
          <p class="text-xs text-n-slate-11">
            {{
              t('AGENTE_IA.BASE.LIMITES_EQUIPE', {
                documentos: limites.documentos_equipe,
              })
            }}
          </p>
        </template>
      </section>

      <Button
        v-if="aba !== 'avisos' && (baseAtual || aba === 'equipe')"
        :label="t('AGENTE_IA.BASE.CRIAR_DOCUMENTO')"
        :disabled="enviando"
        @click="criarDocumento"
      />
      <section
        v-if="aba !== 'avisos' && baseAtual"
        class="flex flex-col gap-3 pt-4 border-t border-n-weak"
      >
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t('AGENTE_IA.BASE.DOCUMENTOS') }} ({{ documentos.length }})
        </h2>
        <p
          v-if="bloqueio"
          class="p-3 text-sm rounded-lg bg-n-amber-3 text-n-amber-11"
        >
          {{ t('AGENTE_IA.BASE.PRECISA_OPENAI') }}
        </p>
        <p v-if="!documentos.length" class="text-sm text-n-slate-11">
          {{ t('AGENTE_IA.BASE.SEM_DOCUMENTOS') }}
        </p>
        <Button
          size="sm"
          variant="outline"
          :label="t('AGENTE_IA.BASE.ATUALIZAR')"
          :disabled="enviando"
          @click="abrirBase(baseAtual)"
        />
        <ul class="flex flex-col gap-1">
          <li
            v-for="documento in documentos"
            :key="documento.id"
            class="flex items-center justify-between gap-3 text-sm text-n-slate-12"
          >
            <span class="truncate">{{ documento.title }}</span>
            <span class="text-xs text-n-slate-11">
              {{
                t(`AGENTE_IA.BASE.STATUS.${documento.status}`, documento.status)
              }}
            </span>
            <Button
              size="sm"
              variant="outline"
              :label="t('AGENTE_IA.BASE.EDITAR')"
              :disabled="enviando"
              @click="editarDocumento(documento)"
            />
            <Button
              size="sm"
              variant="outline"
              :label="t('AGENTE_IA.BASE.EXCLUIR')"
              :disabled="enviando"
              @click="excluindo = documento"
            />
          </li>
        </ul>
        <div
          v-if="excluindo"
          class="flex flex-col gap-2 p-3 rounded-lg border border-n-weak"
        >
          <p class="text-sm text-n-slate-12">
            {{
              t('AGENTE_IA.BASE.CONFIRMAR_EXCLUSAO', {
                titulo: excluindo.title,
              })
            }}
          </p>
          <div class="flex gap-2">
            <Button
              :label="t('AGENTE_IA.BASE.EXCLUIR')"
              :is-loading="enviando"
              :disabled="enviando"
              @click="excluirDocumento(excluindo)"
            />
            <Button
              :label="t('AGENTE_IA.BASE.CANCELAR')"
              variant="outline"
              :disabled="enviando"
              @click="excluindo = null"
            />
          </div>
        </div>
        <div
          v-if="editorAberto"
          class="flex flex-col gap-2 p-3 rounded-lg border border-n-weak"
        >
          <input
            v-model="novoDocumento.titulo"
            :class="classeCampo"
            :aria-label="t('AGENTE_IA.BASE.TITULO_DOCUMENTO')"
            :placeholder="t('AGENTE_IA.BASE.TITULO_DOCUMENTO')"
          />
          <textarea
            v-model="novoDocumento.texto"
            rows="4"
            :class="classeCampo"
            :aria-label="t('AGENTE_IA.BASE.TEXTO_DOCUMENTO')"
            :placeholder="t('AGENTE_IA.BASE.TEXTO_DOCUMENTO')"
          />
          <div class="flex gap-2">
            <Button
              size="sm"
              :label="
                editando
                  ? t('AGENTE_IA.BASE.SALVAR')
                  : t('AGENTE_IA.BASE.CRIAR_DOCUMENTO')
              "
              :is-loading="enviando"
              :disabled="
                enviando ||
                !novoDocumento.titulo.trim() ||
                !novoDocumento.texto.trim()
              "
              @click="enviar()"
            />
            <Button
              size="sm"
              variant="outline"
              :label="t('AGENTE_IA.BASE.CANCELAR')"
              :disabled="enviando"
              @click="editorAberto = false"
            />
            <input
              ref="arquivoInput"
              type="file"
              class="hidden"
              accept=".pdf,.docx,.txt,.md"
              @change="escolherArquivo"
            />
            <Button
              size="sm"
              variant="outline"
              :label="t('AGENTE_IA.BASE.ENVIAR_ARQUIVO')"
              :disabled="enviando || !!editando"
              @click="arquivoInput.click()"
            />
          </div>
        </div>
      </section>

      <section
        v-if="aba === 'agente'"
        class="flex flex-col gap-2 pt-4 border-t border-n-weak"
      >
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t('AGENTE_IA.BASE.APROVACOES.TITULO') }}
        </h2>
        <p class="text-xs text-n-slate-11">
          {{ t('AGENTE_IA.BASE.APROVACOES.AJUDA') }}
        </p>
        <p v-if="!aprovacoes.length" class="text-sm text-n-slate-11">
          {{ t('AGENTE_IA.BASE.APROVACOES.VAZIO') }}
        </p>
        <div
          v-for="aprovacao in aprovacoes"
          :key="aprovacao.id"
          class="flex flex-col gap-2 p-3 rounded-lg border border-n-weak"
        >
          <p class="text-sm font-medium text-n-slate-12">
            {{ aprovacao.titulo }}
          </p>
          <p class="text-sm whitespace-pre-wrap text-n-slate-12">
            {{ aprovacao.conteudo }}
          </p>
          <p v-if="aprovacao.motivo" class="text-xs text-n-slate-11">
            {{ aprovacao.motivo }}
          </p>
          <router-link
            v-if="conversaDoMotivo(aprovacao.motivo)"
            :to="{
              name: 'inbox_conversation',
              params: {
                accountId: route.params.accountId,
                conversation_id: conversaDoMotivo(aprovacao.motivo),
              },
            }"
            class="text-xs font-medium text-n-brand"
          >
            {{ t('AGENTE_IA.BASE.APROVACOES.VER_CONVERSA') }}
          </router-link>
          <div class="flex gap-2">
            <Button
              size="sm"
              :label="t('AGENTE_IA.BASE.APROVACOES.APROVAR')"
              @click="decidir(aprovacao, true)"
            />
            <Button
              size="sm"
              variant="outline"
              :label="t('AGENTE_IA.BASE.APROVACOES.REJEITAR')"
              @click="decidir(aprovacao, false)"
            />
          </div>
        </div>
      </section>
    </template>
  </AgenteIaLayout>
</template>
